import apiClient from "@lift-admin/utils/apiClient";

// Pagination response wrapper
export interface PaginatedResponse<T> {
  total_count: number;
  skip: number;
  limit: number;
  items: T[];
}

// Interface for a backfill operation
export interface BackfillOperation {
  id: number;
  start_time: string;
  end_time: string;
  status: string;
  job_count: number;
  failed_job_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Fetches Adverity backfill operations with pagination
 * @param skip number of records to skip (default 0)
 * @param limit number of records to fetch (default 100)
 */
export const getBackfillOperations = async (
  skip = 0,
  limit = 100
): Promise<PaginatedResponse<BackfillOperation>> => {
  const response = await apiClient.get<PaginatedResponse<BackfillOperation>>(
    "/scrapadverity/api/adverity/backfill-operations",
    { params: { skip, limit } }
  );
  return response.data;
};

// Interface for a backfill job under an operation
export interface BackfillJob {
  id: number;
  operation_id: number;
  job_id: number;
  job_url: string;
  media_platform_account_id: string;
  datastream_id: number;
  datastream_type_id: number;
  status: string;
  job_start: string;
  job_end: string;
  fetch_period_start: string;
  fetch_period_end: string;
  progress: number;
  created_at: string;
  updated_at: string;
  account_name: string;
  formal_name: string;
}

/**
 * Fetches jobs for a given backfill operation with pagination
 * @param operationId - ID of the backfill operation
 * @param skip number of records to skip (default 0)
 * @param limit number of records to fetch (default 100)
 */
export const getBackfillJobsByOperation = async (
  operationId: number,
  skip = 0,
  limit = 100
): Promise<PaginatedResponse<BackfillJob>> => {
  const response = await apiClient.get<PaginatedResponse<BackfillJob>>(
    `/scrapadverity/api/adverity/backfill-operations/${operationId}/jobs`,
    { params: { skip, limit } }
  );
  return response.data;
};

// ============================================
// Run Logs API Types and Functions
// ============================================

// Run type enum values
export type RunType =
  | "authorization_monitor"
  | "datastream_creation"
  | "backup_fetch"
  | "datastream_update"
  | "authorization_sync";

// Run status enum values
export type RunStatus = "running" | "completed" | "failed";

// Interface for run log list item (without log_entries)
export interface RunLogItem {
  run_id: string;
  run_type: RunType;
  status: RunStatus;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
}

// Interface for run log with full log entries
export interface RunLogWithEntries extends RunLogItem {
  log_entries: string | null;
}

// Interface for run log errors response
export interface RunLogErrors {
  run_id: string;
  run_type: RunType;
  status: RunStatus;
  error_count: number;
  errors: string;
}

// Paginated run logs response
export interface PaginatedRunLogs {
  total_count: number;
  skip: number;
  limit: number;
  items: RunLogItem[];
}

/**
 * Fetches paginated list of recent run logs
 * @param skip number of records to skip (default 0)
 * @param limit number of records to fetch (default 50, max 100)
 * @param run_type optional filter by run type
 * @param status optional filter by status
 */
export const getRunLogs = async (
  skip = 0,
  limit = 50,
  run_type?: RunType,
  status?: RunStatus
): Promise<PaginatedRunLogs> => {
  const params: Record<string, any> = { skip, limit };
  if (run_type) params.run_type = run_type;
  if (status) params.status = status;

  const response = await apiClient.get<PaginatedRunLogs>(
    "/scrapadverity/api/adverity/run-logs",
    { params }
  );
  return response.data;
};

/**
 * Fetches a specific run log with all log entries
 * @param runId - UUID of the run to retrieve
 */
export const getRunLogById = async (runId: string): Promise<RunLogWithEntries> => {
  const response = await apiClient.get<RunLogWithEntries>(
    `/scrapadverity/api/adverity/run-logs/${runId}`
  );
  return response.data;
};

/**
 * Fetches only ERROR and WARNING lines from a run
 * @param runId - UUID of the run to retrieve errors for
 */
export const getRunLogErrors = async (runId: string): Promise<RunLogErrors> => {
  const response = await apiClient.get<RunLogErrors>(
    `/scrapadverity/api/adverity/run-logs/${runId}/errors`
  );
  return response.data;
};

// ============================================
// Datastream Event Logs API Types and Functions
// ============================================

// Event type enum values
export type EventType = "callback_received" | "backup_fetch_triggered";

// Event status enum values
export type EventStatus = "success" | "error";

// Interface for event log list item
export interface DatastreamEventLogItem {
  id: number;
  event_type: EventType;
  datastream_id: number | null;
  datastream_type_id: number | null;
  status: EventStatus;
  request_payload: Record<string, any> | null;
  response_payload: Record<string, any> | null;
  error_message: string | null;
  created_at: string;
}

// Interface for event log with log entries (detail view)
export interface DatastreamEventLogDetail extends DatastreamEventLogItem {
  log_entries: string | null;
}

// Paginated event logs response
export interface PaginatedEventLogs {
  total_count: number;
  skip: number;
  limit: number;
  items: DatastreamEventLogItem[];
}

/**
 * Fetches paginated list of datastream event logs
 * @param skip number of records to skip (default 0)
 * @param limit number of records to fetch (default 50, max 200)
 * @param event_type optional filter by event type
 * @param status optional filter by status
 * @param datastream_id optional filter by datastream ID
 */
export const getDatastreamEventLogs = async (
  skip = 0,
  limit = 50,
  event_type?: EventType,
  status?: EventStatus,
  datastream_id?: number
): Promise<PaginatedEventLogs> => {
  const params: Record<string, any> = { skip, limit };
  if (event_type) params.event_type = event_type;
  if (status) params.status = status;
  if (datastream_id) params.datastream_id = datastream_id;

  const response = await apiClient.get<PaginatedEventLogs>(
    "/scrapadverity/api/adverity/datastream-event-logs",
    { params }
  );
  return response.data;
};

/**
 * Fetches a specific event log with all log entries
 * @param eventId - ID of the event to retrieve
 */
export const getDatastreamEventLogById = async (eventId: number): Promise<DatastreamEventLogDetail> => {
  const response = await apiClient.get<DatastreamEventLogDetail>(
    `/scrapadverity/api/adverity/datastream-event-logs/${eventId}`
  );
  return response.data;
};
